import PropTypes from 'prop-types';
import React from 'react';
import { Tooltip } from 'antd';
import styled from 'styled-components';
import { withRouter } from 'utils/withRouter';
import { injectIntl } from 'react-intl';
import { Icon, IconText, Button } from 'ui';
import { withEllipsis } from 'ui/styles';
import qs from 'query-string';
import { transformSAPId, decodeUrlParams } from 'utils/helpers';
import { severities } from 'utils/app-configs';

const ItemWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid ${p => p.theme.colors.border};
  ${p => p.fullPage ? 'min-width: 350px' : 'width: 350px'};
  padding: 10px;
  flex: 0 0 auto;
  ${p => p.fullPage && 'margin-right: 3rem'};
  ${p => p.active && `
    background-color: #ecf2f6;
    border-radius: 4px;
  `};
`;

const ItemBlock = styled.div`
  display: flex;
  flex-direction: column;
  width: 290px;
  cursor: ${p => p.blockRedirect ? 'not-allowed' : 'pointer'};
`;

const HighlightSpan = styled.span`
  > .highlight {
    color: ${p => p.theme.colors.theme};
    font-weight: ${p => p.theme.fontWeight.bold};
  }
`;

const ItemTitle = styled.span`
  > .highlight {
    color: ${p => p.theme.colors.theme};
    font-weight: ${p => p.theme.fontWeight.bold};
  }
  font-size: ${p => p.theme.fontSize.large};
  margin-bottom: 3px;
  font-weight: ${p => p.theme.fontWeight.bolder};
  color: ${p => p.theme.colors.textLight};
  ${withEllipsis};
`;

const SingleLineSpan = styled.span`
  min-height: 24px;
  ${withEllipsis};
`;

const StyledButton = styled(Button)`
  background-color: ${p => !p.isActive ?
    p.theme.colors.brokenWhite.backgroundColor :
    p.theme.colors.theme
  } !important;
  width: 35px !important;
  height: 35px !important;
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  i, img {
    cursor: pointer;
    color: ${p => p.isActive ?
    p.theme.colors.brokenWhite.backgroundColor :
    p.theme.colors.theme} !important;
    width: 20px !important;
    height: 20px !important;
    line-height: 20px;
  }
`;

const SupportingIcon = styled(Icon)`
  color: ${p => p.theme.colors.iconSecondary};
`;

const StyledIconText = styled(IconText)`
  text-transform: uppercase;
  float: right !important;
`;

const SearchItem = ({
  item,
  fullPage,
  history,
  match,
  active = false,
  hasRelations = false,
  intl,
  blockRedirect = false,
}) => {
  const navigateTo = () =>
    history.push(`/${item.uri}/dashboard`, {
      context: item.id,
      technician: '',
    });

  const transformSAPIdLine = (line) => {
    const [before, rest] = line?.split('<span class="highlight">');
    // if there is no highlighting, just transform the id.
    if (!rest) {
      return transformSAPId({ originalId: before });
    }

    const [highlighted, after] = rest?.split('</span>');

    // if the start of the id is highlighted,
    // only try to convert the first part into a SAP id.
    if (!before && rest) {
      return `<span class="highlight">${transformSAPId({
        originalId: highlighted,
      })}</span>${after}`;
    }

    // otherwise,
    // if the highlighted part is not the start,
    // check if the first part is only zeroes.
    // if so, transform both
    // if not, only the first part can be transformed
    if (parseInt(before, 10) === 0) {
      return `${transformSAPId({
        originalId: before,
      })}<span class="highlight">${transformSAPId({
        originalId: highlighted,
      })}</span>${after}`;
    }

    return `${transformSAPId({
      originalId: before,
    })}<span class="highlight">${highlighted}</span>${after}`;
  };

  const toggleShowRelations = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const qParams = qs.parse(history.location.search);
    if (!localStorage.getItem('searchFilters') && qParams?.searchFilters) {
      localStorage.setItem('searchFilters', qParams.searchFilters);
    }
    let url = '';
    let queryString = '';
    if (!match.params.relationId) {
      url = `${history.location.pathname}/relations/${item.id}`;
    } else if (item.id === match.params.relationId) {
      url = history.location.pathname.replace(/\/relations.*/, '');
      if (localStorage.getItem('searchFilters')) {
        queryString = localStorage.getItem('searchFilters');
      }
    } else {
      url = history.location.pathname.replace(/relations\/.*?(?=\/|$)/, `relations/${item.id}`);
    }
    if (url) {
      history.push(url, {
        searchFilters: queryString ? encodeURIComponent(queryString) : '',
      });
    }
  };

  const renderMenu = () => {
    const params = decodeUrlParams(match.params);
    if (hasRelations) {
      return (
        <Tooltip
          title={item.id === match.params.relationId ?
            // eslint-disable-next-line
            `${intl.formatMessage({ id: 'search.BACK_TO_OVERVIEW' })} ${intl.formatMessage({ id: 'search.FOR_TERM' }, { term: params.searchValue })}` :
            intl.formatMessage({ id: 'search.SHOW_RELATED_OBJECTS' })
          }
          mouseEnterDelay={0.75}
        >
          <StyledButton
            onClick={toggleShowRelations}
            isActive={item.id === match.params.relationId}
          >
            {item.id === match.params.relationId ?
              <Icon
                type='project-diagram-cross'
                customIcon={true}
              /> :
              <Icon type='project-diagram' size='lg' />
            }
          </StyledButton>
        </Tooltip >
      );
    }
    return null;
  };

  const renderTitleLine = (line, icon) => {
    if (line) {
      if (icon) {
        return (
          <SingleLineSpan>
            <Icon type={icon} marginRight />
            <ItemTitle dangerouslySetInnerHTML={{ __html: line }} />
          </SingleLineSpan>
        );
      }
      return (
        <SingleLineSpan>
          <ItemTitle dangerouslySetInnerHTML={{ __html: line }} />
        </SingleLineSpan>
      );
    }

    return null;
  };

  const renderLine = (line, icon, isSapId, isInactive) => {
    let transformedLine = line;
    if (isSapId && line) {
      const splitLine = line?.split('|');
      transformedLine =
        `${transformSAPIdLine(splitLine[0])}${splitLine[1] ? ` | ${splitLine[1]}` : ''}`;
    }

    if (icon) {
      return (
        <SingleLineSpan>
          <SupportingIcon type={icon} marginRight />
          <HighlightSpan dangerouslySetInnerHTML={{ __html: transformedLine }} />
          {isInactive &&
            <StyledIconText
              iconType='lock'
              severity={severities.INACTIVE}
              translationKey='search.INACTIVE'
            />
          }
        </SingleLineSpan>
      );
    }
    return (
      <SingleLineSpan>
        <HighlightSpan dangerouslySetInnerHTML={{ __html: transformedLine }} />
        {isInactive &&
          <StyledIconText
            iconType='lock'
            severity={severities.INACTIVE}
            translationKey='search.INACTIVE'
          />
        }
      </SingleLineSpan>
    );
  };

  return (
    <ItemWrapper fullPage={fullPage} active={active} data-test-id='search-result-item'>
      <ItemBlock
        onClick={() => !blockRedirect && navigateTo(item)}
        blockRedirect={blockRedirect}
      >
        {renderTitleLine(item.line1, item.line1Icon)}
        {renderLine(item.line2, item.line2Icon, true, item.isInactive || false)}
        {renderLine(item.line3, item.line3Icon)}
        {renderLine(item.line4, item.line4Icon)}
      </ItemBlock>
      {renderMenu()}
    </ItemWrapper>
  );
};
SearchItem.propTypes = {
  item: PropTypes.object,
  fullPage: PropTypes.bool,
  history: PropTypes.object.isRequired,
  match: PropTypes.object,
  active: PropTypes.bool,
  hasRelations: PropTypes.bool,
  intl: PropTypes.object,
  blockRedirect: PropTypes.bool,
};

export default withRouter(injectIntl(SearchItem));
